import React, { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Lock,
  Play,
  Radio,
  Clock,
  ShieldCheck,
  RotateCcw,
  Ticket,
} from "lucide-react";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { useTheme } from "@/context/ThemeContext";
import { Countdown } from "@/components/Countdown";
import { LiveChatPlaceholder } from "@/components/live/LiveChatPlaceholder";
import { EventStatusBadge } from "@/components/EventStatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Logo } from "@/components/Logo";
import { ownsEvent, bestTypeForEvent, computeStatus } from "@/lib/tickets";

function Shell({ children }) {
  return <div className="min-h-screen bg-[#0A1526] text-white">{children}</div>;
}

function TopBar() {
  const navigate = useNavigate();
  return (
    <div className="sticky top-0 z-50 flex items-center justify-between border-b border-white/10 bg-black/60 px-5 py-3 backdrop-blur-xl sm:px-8">
      <button
        data-testid="access-back-btn"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <Link to="/" className="flex items-center">
        <Logo size={32} wordmarkColor="text-white" />
      </Link>
    </div>
  );
}

export default function PremiumLive() {
  const { eventId } = useParams();
  const { setTheme } = useTheme();

  useEffect(() => {
    setTheme("dark");
    return () => setTheme("light");
  }, [setTheme]);

  const { data: event, loading } = useMockQuery(
    () => apiClient.getEvent(eventId),
    [eventId]
  );

  if (loading)
    return (
      <Shell>
        <TopBar />
        <div className="mx-auto max-w-5xl px-5 py-8">
          <Skeleton className="aspect-video w-full rounded-2xl bg-white/5" />
        </div>
      </Shell>
    );

  if (!event || event.type !== "premium")
    return (
      <Shell>
        <TopBar />
        <div className="mx-auto max-w-lg px-5 py-24 text-center">
          <p className="text-white/70">This live experience is not available.</p>
        </div>
      </Shell>
    );

  const owns = ownsEvent(event.id);
  const type = bestTypeForEvent(event.id);
  const status = owns ? computeStatus(event, type) : { phase: "no-ticket" };
  const phase = status.phase;

  // --- Locked: no valid ticket ---
  if (phase === "no-ticket") {
    return (
      <Shell>
        <TopBar />
        <div className="mx-auto flex max-w-lg flex-col items-center px-5 py-24 text-center">
          <span className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/10 text-white">
            <Lock className="h-7 w-7" />
          </span>
          <EventStatusBadge status="premium" />
          <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-tight">
            {event.title}
          </h1>
          <p className="mt-3 text-white/70">
            You need a valid ticket to enter this premium live experience. The stream is
            private and not publicly accessible.
          </p>
          <Button asChild className="mt-8 rounded-full bg-brand px-7 py-6 text-base hover:bg-brand-hover">
            <Link to={`/event/${event.id}`}>
              <Ticket className="mr-2 h-5 w-5" /> Get a ticket
            </Link>
          </Button>
        </div>
      </Shell>
    );
  }

  // --- Before: access opens soon ---
  if (phase === "upcoming") {
    return (
      <Shell>
        <TopBar />
        <div className="mx-auto max-w-3xl px-5 py-16 text-center">
          <div className="mb-3 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold text-white">
              <ShieldCheck className="h-4 w-4 text-emerald-400" /> Ticket verified
            </span>
          </div>
          <p className="flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-wide text-brand">
            <Clock className="h-4 w-4" /> Access opens soon
          </p>
          <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
            {event.title}
          </h1>
          <p className="mt-2 text-white/60">
            The live experience will unlock automatically when the event begins.
          </p>
          <div className="mt-8 flex justify-center">
            <Countdown target={event.startsAt} />
          </div>
          <div className="relative mx-auto mt-10 aspect-video max-w-2xl overflow-hidden rounded-2xl">
            <img src={event.cover} alt="" className="h-full w-full object-cover opacity-40 blur-[1px]" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10 text-white/70">
                <Lock className="h-7 w-7" />
              </span>
            </div>
          </div>
        </div>
      </Shell>
    );
  }

  // --- Ended, no replay ---
  if (phase === "expired") {
    return (
      <Shell>
        <TopBar />
        <div className="mx-auto max-w-lg px-5 py-24 text-center">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">{event.title}</h1>
          <p className="mt-3 text-white/70">
            This event has ended and a replay was not included with your ticket.
          </p>
          <Button asChild variant="outline" className="mt-8 rounded-full border-white/20 bg-white/5 text-white hover:bg-white/10">
            <Link to="/replay">Browse replays</Link>
          </Button>
        </div>
      </Shell>
    );
  }

  // --- Active (live) or Replay: show the experience ---
  const isReplay = phase === "replay";
  return (
    <Shell>
      <TopBar />
      <div className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6">
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          <div className="space-y-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.99 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="relative aspect-video overflow-hidden rounded-2xl border border-white/10 bg-black"
            >
              <img src={event.cover} alt={event.title} className="h-full w-full object-cover opacity-80" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <button
                  data-testid="access-play-btn"
                  className="flex h-20 w-20 items-center justify-center rounded-full bg-brand/90 text-white shadow-2xl transition-transform duration-300 hover:scale-105"
                >
                  <Play className="ml-1 h-8 w-8 fill-white" />
                </button>
              </div>
              <div className="absolute left-4 top-4 flex items-center gap-2">
                {isReplay ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-500 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                    <RotateCcw className="h-3.5 w-3.5" /> Replay
                  </span>
                ) : (
                  <EventStatusBadge status="live" />
                )}
              </div>
              <div className="absolute bottom-4 left-4 rounded-full bg-black/50 px-3 py-1 text-xs text-neutral-200 backdrop-blur-md">
                Private stream · gated by ticket · LiveKit not connected (demo)
              </div>
            </motion.div>

            <div className="rounded-2xl border border-white/10 bg-[#111f38] p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h1 className="font-heading text-xl font-extrabold tracking-tight sm:text-2xl">
                    {event.title}
                  </h1>
                  <p className="mt-1 text-sm text-neutral-400">
                    {event.location.name}, {event.location.city} · {event.category}
                  </p>
                </div>
                {isReplay && status.replayExpiresAt && (
                  <span className="flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-sm text-white/70">
                    Replay expires in{" "}
                    <Countdown target={status.replayExpiresAt} variant="inline" className="font-semibold text-white" />
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="h-[560px] lg:h-auto">
            <LiveChatPlaceholder viewers={isReplay ? undefined : event.viewers || 1840} />
          </div>
        </div>
      </div>
    </Shell>
  );
}
